import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import { useParams } from "react-router-dom";

import BucketAction from "../actions/BucketAction";
import { useQuery } from "../AppLayout";
import DataBucketRow, { ColorDot, getNextAvailableColor } from "./components/DataBucketRow";
import DataBucketRowSkeleton from "./components/DataBucketRowSkeleton";

const BUCKET_TYPE = "SALE";
const SET_UP_MODE = false;

const normalizeWeightInput = (value?: string): number => {
  if (!value || value.trim() === "") {
    return 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const deriveScoringPreview = (rawWeight?: string | number) => {
  const parsedWeight = normalizeWeightInput(String(rawWeight ?? 0));
  const intentSignals = Math.trunc(parsedWeight);
  const cappedIntentSignals = Math.min(intentSignals, 10);
  const connectionIntentContribution = cappedIntentSignals * 3.0;
  const estimatedProbabilityIntentContribution = Math.min(Math.max(intentSignals / 10, 0), 1) * 30;
  return {
    parsedWeight,
    intentSignals,
    cappedIntentSignals,
    connectionIntentContribution,
    estimatedProbabilityIntentContribution,
  };
};

const SaleFunnelSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId = "" } = useParams<{ projectId: string }>();
  const tab = useQuery().get("item");
  const [selectedStageId, setSelectedStageId] = useState<string>("");
  const [activeItemId, setActiveItemId] = useState<string>("");
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [editingColors, setEditingColors] = useState<Record<string, string>>({});
  const [editingWeights, setEditingWeights] = useState<Record<string, string>>({});
  const [newValue, setNewValue] = useState("");
  const [newColor, setNewColor] = useState("");
  const [newWeight, setNewWeight] = useState("0");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reorderingStageId, setReorderingStageId] = useState<string | null>(null);
  const [reorderingDirection, setReorderingDirection] = useState<"up" | "down" | null>(null);

  const buckets = useSelector((state: any) => state.bucketReducer.get(BUCKET_TYPE)) || fromJS([]);

  const stageList = useMemo(
    () => (typeof buckets.toJS === "function" ? buckets.toJS() : []),
    [buckets]
  );

  // Sync editing values and colors from store
  useEffect(() => {
    const nextValues: Record<string, string> = {};
    const nextColors: Record<string, string> = {};
    const nextWeights: Record<string, string> = {};
    stageList.forEach((stage: any) => {
      const stageId = stage.id || stage.get?.("id");
      const stageName = stage.name || stage.get?.("name") || "";
      const stageColor = stage.color || stage.get?.("color") || "";
      const stageWeight = stage.weight ?? stage.get?.("weight") ?? 0;
      if (stageId) {
        nextValues[stageId] = stageName;
        nextColors[stageId] = stageColor;
        nextWeights[stageId] = String(stageWeight);
      }
    });
    setEditingValues(nextValues);
    setEditingColors(nextColors);
    setEditingWeights(nextWeights);
  }, [stageList]);

  const selectedStage = useMemo(
    () => stageList.find((stage) => stage.id === selectedStageId),
    [selectedStageId, stageList]
  );

  const closureStageId = stageList.length > 0 ? stageList[stageList.length - 1]?.id : "";

  useEffect(() => {
    if (projectId) {
      setIsLoading(true);
      const dispatchPromise = dispatch(BucketAction.getBuckets(projectId, BUCKET_TYPE) as any);

      if (dispatchPromise && typeof dispatchPromise.then === "function") {
        dispatchPromise.finally(() => {
          setIsLoading(false);
        });
      } else {
        setTimeout(() => {
          setIsLoading(false);
        }, 100);
      }
    }
  }, [dispatch, projectId, tab]);

  useEffect(() => {
    if (!buckets || buckets.size === 0) {
      setSelectedStageId("");
      return;
    }
    const exists = buckets.find((bucket: any) => bucket.get("id") === selectedStageId) || null;
    if (!exists) {
      const lastStage = buckets.get(buckets.size - 1);
      setSelectedStageId(lastStage?.get("id") ?? "");
    }
  }, [buckets, selectedStageId]);

  const handleChangeBucket = useCallback(
    ({
      value,
      id,
      color,
      weight,
    }: {
      value: string;
      id?: string;
      color?: string;
      weight?: string;
    }) => {
      if (!projectId || !value.trim()) {
        return;
      }
      const bucket =
        buckets.filter((item: any) => item.get("id") === id).first() || fromJS({ id: "-1" });
      let data = {
        ...bucket.toJS(),
        name: value.trim(),
        color: color ?? bucket.get("color") ?? "",
        weight: normalizeWeightInput(weight ?? String(bucket.get("weight") ?? 0)),
        projectId,
        type: BUCKET_TYPE,
        setUp: SET_UP_MODE,
      };
      if (bucket.get("id") === "-1" || !id) {
        data = {
          id: undefined,
          projectId,
          tenantId: "",
          name: value.trim(),
          color: color || "",
          weight: normalizeWeightInput(weight),
          order: buckets.size,
          type: BUCKET_TYPE,
          setUp: SET_UP_MODE,
        };
      }
      dispatch(BucketAction.saveBucket(data));
    },
    [buckets, dispatch, projectId]
  );

  const handleFinish = useCallback(
    (value: string, id?: string, color?: string, weight?: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        if (!id) {
          setNewValue("");
          setNewColor("");
          setNewWeight("0");
        }
        setActiveItemId("");
        return;
      }
      handleChangeBucket({ value: trimmed, id, color, weight });
      if (!id) {
        setNewValue("");
        setNewColor("");
        setNewWeight("0");
      }
      setActiveItemId("");
    },
    [handleChangeBucket]
  );

  const handleColorChange = useCallback(
    (stageId: string, color: string) => {
      setEditingColors((prev) => ({ ...prev, [stageId]: color }));
      // Auto-save color change immediately
      const name = editingValues[stageId];
      if (name && stageId) {
        handleChangeBucket({ value: name, id: stageId, color, weight: editingWeights[stageId] });
      }
    },
    [editingValues, editingWeights, handleChangeBucket]
  );

  const handleDeleteStage = useCallback(
    (stageId: string) => {
      dispatch(BucketAction.deleteBucket(stageId, BUCKET_TYPE, SET_UP_MODE));
      if (selectedStageId === stageId) {
        setSelectedStageId("");
      }
    },
    [dispatch, selectedStageId]
  );

  const handleMoveStage = useCallback(
    (stageId: string, direction: "up" | "down") => {
      if (!projectId || reorderingStageId) {
        return;
      }
      setReorderingStageId(stageId);
      setReorderingDirection(direction);
      const dispatchPromise = dispatch(
        BucketAction.changeBucketOrder({
          projectId,
          bucketId: stageId,
          direction,
          type: BUCKET_TYPE,
          setUp: SET_UP_MODE,
        }) as any
      );

      if (dispatchPromise && typeof dispatchPromise.then === "function") {
        dispatchPromise
          .then(() => {
            dispatch(BucketAction.getBuckets(projectId, BUCKET_TYPE));
          })
          .finally(() => {
            setReorderingStageId(null);
            setReorderingDirection(null);
          });
      } else {
        setTimeout(() => {
          setReorderingStageId(null);
          setReorderingDirection(null);
        }, 100);
      }
    },
    [dispatch, projectId, reorderingStageId]
  );

  const handleAddStage = useCallback(() => {
    // Auto-assign next available color
    const usedColors = stageList.map((item: any) => item.color || item.get?.("color") || "");
    const nextColor = getNextAvailableColor(usedColors);
    setActiveItemId("new");
    setNewValue("");
    setNewColor(nextColor);
    setNewWeight("0");
  }, [stageList]);

  const handleSelectStage = useCallback((stageId: string) => {
    setSelectedStageId(stageId);
  }, []);

  const closureInsights = useMemo(
    () => [
      {
        id: "won",
        labelKey: "WON",
        description: `${t(
          "The closed stages are used when the deal has reached its conclusion (successfully or not)."
        )} ${t("If you won the deal, you move it to the Won stage.")}`,
        badgeClasses: "bg-emerald-100 text-emerald-900",
      },
      {
        id: "unqualified",
        labelKey: "UNQUALIFIED",
        description: t(
          "You might move a deal to the Unqualified stage to signify that the potential business opportunity was unsuccessful because of a lack of interest from the contact or because delays or losses of the contact's budget and so on."
        ),
        badgeClasses: "bg-amber-100 text-amber-900",
      },
      {
        id: "lost",
        labelKey: "LOST",
        description: t(
          "If you lost the deal (for example, to a competitor) you move the deal to the Lost stage."
        ),
        badgeClasses: "bg-rose-100 text-rose-900",
      },
    ],
    [t]
  );

  const isClosureSelected = selectedStageId !== "" && selectedStageId === closureStageId;
  const selectedStageWeightValue =
    selectedStage && selectedStage.id
      ? (editingWeights[selectedStage.id] ?? String(selectedStage.weight ?? 0))
      : "0";
  const scoringPreview = deriveScoringPreview(selectedStageWeightValue);

  return (
    <div className="max-w-5xl py-6">
      <div className="flex flex-col gap-1 sm:px-0">
        <h3 className="text-lg font-semibold text-gray-900">{t("Sale Funnels")}</h3>
        <p className="text-sm text-gray-500">{t("Manage pipeline members, set access levels")}</p>
      </div>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="w-full space-y-4 bg-white lg:max-w-md">
          <button
            type="button"
            onClick={handleAddStage}
            disabled={isLoading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-blue-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4" />
            {t("Add New")}
          </button>

          {isLoading ? (
            <DataBucketRowSkeleton count={3} showPrefix={false} rowClassName="px-4 py-3" />
          ) : (
            <div className="space-y-3">
              {stageList.map((stage, index) => {
                const rowKey =
                  stage?.id || stage?.temporaryId || `${stage?.name || "stage"}-${index}`;
                const isActive = stage.id === selectedStageId;
                const stageValue =
                  editingValues[stage.id] ?? stage.name ?? stage.get?.("name") ?? "";
                const stageColor =
                  editingColors[stage.id] ?? stage.color ?? stage.get?.("color") ?? "";
                const stageWeight =
                  editingWeights[stage.id] ?? String(stage.weight ?? stage.get?.("weight") ?? 0);
                const stageScoringPreview = deriveScoringPreview(stageWeight);
                const stageWeightSummary = `Strength +${stageScoringPreview.connectionIntentContribution.toFixed(1)} | Probability +${stageScoringPreview.estimatedProbabilityIntentContribution.toFixed(1)}%`;

                const isReordering = reorderingStageId === stage.id;
                const isReorderingUp = isReordering && reorderingDirection === "up";
                const isReorderingDown = isReordering && reorderingDirection === "down";

                const footer =
                  stage.id === closureStageId ? (
                    <span className="inline-flex max-w-max rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                      {t("Closure Stage")}
                    </span>
                  ) : null;

                return (
                  <DataBucketRow
                    key={`${rowKey}-${index}`}
                    index={index}
                    bucketId={stage.id}
                    bucketValue={stageValue}
                    bucketColor={stageColor}
                    bucketWeight={stageWeight}
                    weightSummary={stageWeightSummary}
                    placeholder="Name"
                    isActive={isActive}
                    onClick={() => handleSelectStage(stage.id)}
                    className="px-4 py-3"
                    bodyClassName="items-center"
                    isLoading={isLoading}
                    isReordering={isReordering}
                    isReorderingUp={isReorderingUp}
                    isReorderingDown={isReorderingDown}
                    canMoveUp={index > 0}
                    canMoveDown={index < stageList.length - 1}
                    onInputChange={(value) =>
                      setEditingValues((prev) => ({
                        ...prev,
                        [stage.id]: value,
                      }))
                    }
                    onColorChange={(color) => handleColorChange(stage.id, color)}
                    onInputFocus={() => setActiveItemId(stage.id)}
                    onInputBlur={() => handleFinish(stageValue, stage.id, stageColor, stageWeight)}
                    onWeightChange={(value) =>
                      setEditingWeights((prev) => ({
                        ...prev,
                        [stage.id]: value,
                      }))
                    }
                    onWeightBlur={() => handleFinish(stageValue, stage.id, stageColor, stageWeight)}
                    onInputKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleFinish(stageValue, stage.id, stageColor, stageWeight);
                      }
                    }}
                    onWeightKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleFinish(stageValue, stage.id, stageColor, stageWeight);
                      }
                    }}
                    onReorderUp={() => handleMoveStage(stage.id, "up")}
                    onReorderDown={() => handleMoveStage(stage.id, "down")}
                    onDelete={() => handleDeleteStage(stage.id)}
                    footer={footer}
                    t={t}
                  />
                );
              })}

              {activeItemId === "new" && (
                <div className="flex items-center gap-1 rounded-2xl border border-dashed border-indigo-200 bg-white px-4 py-3 shadow-sm">
                  <ColorDot
                    color={newColor}
                    onChange={(c) => setNewColor(c)}
                    disabled={isLoading}
                  />
                  <input
                    value={newValue}
                    autoFocus
                    placeholder={t("New Funnel Stage")}
                    disabled={isLoading}
                    onChange={(event) => setNewValue(event.target.value)}
                    onBlur={() => handleFinish(newValue, undefined, newColor)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleFinish(newValue, undefined, newColor);
                      }
                    }}
                    className="flex-1 rounded-xl border border-transparent px-3 py-2 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newWeight}
                    placeholder={t("Weight")}
                    disabled={isLoading}
                    onChange={(event) => setNewWeight(event.target.value)}
                    onBlur={() => handleFinish(newValue, undefined, newColor, newWeight)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleFinish(newValue, undefined, newColor, newWeight);
                      }
                    }}
                    className="w-24 rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <button
                    type="button"
                    className="rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                    onClick={() => {
                      setActiveItemId("");
                      setNewValue("");
                      setNewColor("");
                      setNewWeight("0");
                    }}
                    aria-label={t("Close")}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}

              {stageList.length === 0 && activeItemId !== "new" && (
                <div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                  {t("No stages")}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="w-full rounded-xl bg-slate-50 p-5 shadow-inner">
          <div className="mb-5 space-y-1">
            <p className="text-sm font-semibold text-gray-900">
              {selectedStage?.name || t("Sale Funnels")}
            </p>
            <p className="text-xs text-gray-500">
              {selectedStage?.description || t("Sale funnels information")}
            </p>
          </div>

          <div className="">
            <div className="mb-5 rounded-lg border border-indigo-100 bg-white p-4">
              <p className="text-sm font-semibold text-gray-900">{t("Scoring Preview")}</p>
              <p className="mt-1 text-xs text-gray-500">
                {t(
                  "Weight feeds intent signals in contact scoring. This preview follows backend formulas."
                )}
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-700 sm:grid-cols-2">
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <span className="font-medium text-gray-900">{t("Raw Weight")}: </span>
                  {scoringPreview.parsedWeight}
                </div>
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <span className="font-medium text-gray-900">{t("Intent Signals")}: </span>
                  {scoringPreview.intentSignals}
                </div>
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <span className="font-medium text-gray-900">
                    {t("Connection Intent Score")}:{" "}
                  </span>
                  {`${scoringPreview.cappedIntentSignals} x 3 = ${scoringPreview.connectionIntentContribution.toFixed(2)}`}
                </div>
                <div className="rounded-md bg-gray-50 px-3 py-2">
                  <span className="font-medium text-gray-900">
                    {t("Estimated Probability Intent Share")}:
                  </span>{" "}
                  {`${scoringPreview.estimatedProbabilityIntentContribution.toFixed(1)}%`}
                </div>
              </div>
              <p className="mt-2 text-[11px] text-gray-500">
                {t(
                  "Connection strength uses min(intentSignals, 10). Estimated probability uses intentSignals / 10 and contributes up to 30%."
                )}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-900">{t("About Closure Stage")}</p>
              <p className="text-xs text-gray-500">
                {t(
                  "In the Closure Stage, you negotiate the final terms of and seek to close the deal."
                )}
              </p>
            </div>
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-4 gap-y-4">
                {closureInsights.map((insight) => (
                  <React.Fragment key={insight.id}>
                    <div className="flex items-start justify-start sm:justify-end">
                      <span
                        className={`inline-flex items-center h-6 justify-center rounded-full px-3 text-xs font-semibold whitespace-nowrap ${insight.badgeClasses}`}
                      >
                        {t(insight.labelKey)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{insight.description}</p>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {isClosureSelected && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-indigo-700">
              {t("Closure Stage")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaleFunnelSetting;
